import * as React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { AxiosError } from "axios";
import SlideOverPanel from "components/SlideOverPanel";
import AddSubscription from "./AddSubscription";
import EditSubscription from "./EditSubscription";

const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// ---------- TYPE ALIASES ----------

type DateInput = string | number | Date;

// ---------- INTERFACES ----------

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

interface SubscriptionTypeItem {
  subscription_type_id: number;
  subscription_type_name: string;
}

interface CustomerItem {
  customer_id: number;
  name: string;
}

interface TableMetaData {
  currentTableData: Array<{ index: number }>;
  rowIndex: number;
  tableData: any[];
}

interface SubscriptionData {
  subscription_id: number;
  is_active: boolean;
}

// ---------- EXTRACTED COMPONENTS ----------

interface StatusCellProps {
  value: string;
}

const StatusCell: React.FC<StatusCellProps> = ({ value }) => (
  <span
    className="active-btn"
    style={{
      borderColor: value ? "var(--primary-color, #2e7d32)" : "#fe346e",
    }}
  >
    {value ? "Active" : "Inactive"}
  </span>
);

interface ActionsCellProps {
  subscription: SubscriptionData;
  onEdit: (subscriptionId: number) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({ subscription, onEdit }) => (
  <Box>
    {subscription.is_active ? (
      <IconButton
        aria-label="edit"
        onClick={() => onEdit(subscription.subscription_id)}
      >
        <EditIcon />
      </IconButton>
    ) : (
      ""
    )}
  </Box>
);

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

const SubscriptionList = () => {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [assetTypes, setAssetTypes] = React.useState<AssetTypeItem[] | null>(null);
  const [subscriptionTypes, setSubscriptionTypes] = React.useState<
    SubscriptionTypeItem[] | null
  >(null);
  const [customers, setCustomers] = React.useState<CustomerItem[] | null>(null);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);
  const [subscriptionTypeFilter, setSubscriptionTypeFilter] = React.useState<
    SubscriptionTypeItem | null
  >(null);
  const [customerFilter, setCustomerFilter] =
    React.useState<CustomerItem | null>(null);
  const [addPanelOpen, setAddPanelOpen] = React.useState(false);
  const [editPanelOpen, setEditPanelOpen] = React.useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = React.useState<number | null>(null);

  const hasActiveFilters =
    assetTypeFilter != null ||
    subscriptionTypeFilter != null ||
    customerFilter != null;

  const clearFilters = () => {
    setAssetTypeFilter(null);
    setSubscriptionTypeFilter(null);
    setCustomerFilter(null);
  };

  const formatDate = (dateString: DateInput) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "short",
      day: "2-digit",
    };
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", options).format(date);
  };

  const handleEditSubscription = (subscriptionId: number) => {
    setSelectedSubscriptionId(subscriptionId);
    setEditPanelOpen(true);
  };

  // Render functions for table columns
  const renderStartDate = (value: DateInput) => {
    return formatDate(value);
  };

  const renderEndDate = (value: DateInput) => {
    return formatDate(value);
  };

  const renderStatusCell = (value: string) => {
    return <StatusCell value={value} />;
  };

  const renderActionsCell = (value: any, tableMeta: TableMetaData) => {
    const { currentTableData, rowIndex } = tableMeta;
    const subscription =
      tableMeta?.tableData[currentTableData[rowIndex]?.index];
    return (
      <ActionsCell
        subscription={subscription}
        onEdit={handleEditSubscription}
      />
    );
  };

  const columns: MUIDataTableColumn[] = [
    { name: "name", label: "Customer" },
    { name: "asset_service_id", label: "Asset Id" },
    { name: "asset_type_name", label: "Asset Type" },
    { name: "subscription_type", label: "Subscription Type" },
    {
      name: "start_date",
      label: "Start Date",
      options: {
        filter: false,
        customBodyRender: renderStartDate,
      },
    },
    {
      name: "end_date",
      label: "End Date",
      options: {
        filter: false,
        customBodyRender: renderEndDate,
      },
    },
    {
      name: "is_active",
      label: "Status",
      options: {
        filter: false,
        customBodyRender: renderStatusCell,
      },
    },
    {
      name: "id",
      label: "Actions",
      options: {
        filter: false,
        sort: false,
        customBodyRender: renderActionsCell,
      },
    },
  ];

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get("/subscription", {
        params: {
          filters: {
            asset_type_id: assetTypeFilter?.asset_type_id,
            subscription_type_id: subscriptionTypeFilter?.subscription_type_id,
            customer_id: customerFilter?.customer_id,
          },
        },
      });

      setRows(response?.data);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, [assetTypeFilter, subscriptionTypeFilter, customerFilter]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchFiltersData = async () => {
    try {
      const [assetTypesRes, subscriptionTypesRes, customersRes] =
        await Promise.all([
          axios.get("/asset-types"),
          axios.get("/subscription-types"),
          axios.get("/customer"),
        ]);
      setAssetTypes(assetTypesRes?.data?.list ?? []);
      setSubscriptionTypes(subscriptionTypesRes?.data?.list ?? []);
      setCustomers(customersRes?.data?.list ?? []);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchFiltersData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-end" }}>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Asset Types
              </SoftTypography>
              <Autocomplete
                options={assetTypes ?? []}
                getOptionLabel={(option) => option.asset_type_name ?? ""}
                value={assetTypeFilter}
                onChange={(event, value) => setAssetTypeFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Subscription Type
              </SoftTypography>
              <Autocomplete
                options={subscriptionTypes ?? []}
                getOptionLabel={(option) => option.subscription_type_name ?? ""}
                value={subscriptionTypeFilter}
                onChange={(event, value) => setSubscriptionTypeFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select type"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl sx={{ width: 200, minWidth: 200, flexShrink: 0 }}>
              <SoftTypography
                variant="caption"
                fontWeight="bold"
                sx={{ paddingBottom: "6px" }}
              >
                Customer
              </SoftTypography>
              <Autocomplete
                options={customers ?? []}
                getOptionLabel={(option) => option.name ?? ""}
                value={customerFilter}
                onChange={(event, value) => setCustomerFilter(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select customer"
                    sx={{
                      "& .MuiInputBase-input": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                )}
              />
            </FormControl>
            {hasActiveFilters && (
              <Button variant="outlined" color="primary" onClick={clearFilters} size="small">
                Clear Filter
              </Button>
            )}
          </Box>
          <SoftButton
            variant="gradient"
            color="info"
            onClick={() => setAddPanelOpen(true)}
          >
            Add Subscription
          </SoftButton>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-subscriptions"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Subscriptions"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
      <SlideOverPanel
        open={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        title="Add Subscription"
        width={1000}
      >
        <AddSubscription
          embedded
          onSuccess={() => {
            setAddPanelOpen(false);
            fetchData();
          }}
          onCancel={() => setAddPanelOpen(false)}
        />
      </SlideOverPanel>
      <SlideOverPanel
        open={editPanelOpen}
        onClose={() => {
          setEditPanelOpen(false);
          setSelectedSubscriptionId(null);
        }}
        title="Edit Subscription"
        width={1000}
      >
        {selectedSubscriptionId != null && (
          <EditSubscription
            id={String(selectedSubscriptionId)}
            embedded
            onSuccess={() => {
              setEditPanelOpen(false);
              setSelectedSubscriptionId(null);
              fetchData();
            }}
            onCancel={() => {
              setEditPanelOpen(false);
              setSelectedSubscriptionId(null);
            }}
          />
        )}
      </SlideOverPanel>
    </DashboardLayout>
  );
};

export default SubscriptionList;
